import { Search } from '@carbon/icons-react';
import * as React from 'react';
import {
  Checkbox,
  ChipInput,
  Input,
  InputWrapper,
  Radio,
  Selector,
  TableBody,
} from 'vet-bones/design-system/components';
import { SelectItem } from 'vet-bones/design-system/utils/select';

import {
  INVITE_ORGANIZATION_DIALOG_CONNECTIONS_DEFAULT,
  INVITE_ORGANIZATION_DIALOG_CONNECTIONS_SEARCH,
  INVITE_ORGANIZATION_DIALOG_CONNECTIONS_SEARCH_CLEAR,
  INVITE_ORGANIZATION_DIALOG_CONNECTIONS_SELECT,
  INVITE_ORGANIZATION_DIALOG_CONNECTION_CHECKBOX,
  INVITE_ORGANIZATION_DIALOG_CONNECTION_ROLES,
  INVITE_ORGANIZATION_DIALOG_CONNECTION_ROLE_ITEM,
} from 'src/ui/constants/testIds';
import { getEndpointUrl } from 'src/ui/containers/endpoints/utils/getEndpointUrl';
import { Connection } from 'src/ui/graph/types';
import * as copy from 'src/ui/templates/copy';

export interface InviteOrganizationSelectConnectionsStepProps {
  connections: Connection[];
  connectionSearch: string;
  defaultRolesByConnectionId: Record<string, string[]>;
  isDefaultConnections: boolean;
  onSetConnectionSearch: React.Dispatch<React.SetStateAction<string>>;
  onSetIsDefaultConnections: React.Dispatch<React.SetStateAction<boolean>>;
  onSetSelectedConnectionIds: React.Dispatch<React.SetStateAction<string[]>>;
  onSetSelectedConnectionRolesById: React.Dispatch<
    React.SetStateAction<Record<string, string[]>>
  >;
  rolesByConnectionId: Record<
    string,
    { isLoading: boolean; roles: Set<string> | null }
  >;
  selectedConnectionIds: string[];
  selectedConnectionRolesById: Record<string, string[]>;
}

export const InviteOrganizationSelectConnectionsStep: React.VFC<InviteOrganizationSelectConnectionsStepProps> = ({
  connections,
  connectionSearch,
  defaultRolesByConnectionId,
  isDefaultConnections,
  onSetConnectionSearch,
  onSetIsDefaultConnections,
  onSetSelectedConnectionIds,
  onSetSelectedConnectionRolesById: onSetSelectedConnectionRolesByIds,
  rolesByConnectionId,
  selectedConnectionIds,
  selectedConnectionRolesById,
}) => {
  const defaultConnectionIds = React.useMemo(
    () => new Set(Object.keys(defaultRolesByConnectionId)),
    [defaultRolesByConnectionId]
  );

  const filteredItems = React.useMemo(() => {
    const allItems = isDefaultConnections
      ? connections.filter((conn) => defaultConnectionIds.has(conn.id))
      : connections;

    if (!connectionSearch.trim()) {
      return allItems;
    }

    const query = connectionSearch.trim().toLowerCase();
    return allItems.filter(
      (conn) =>
        conn.name.toLowerCase().includes(query) ||
        conn.endpoint.toLowerCase().includes(query)
    );
  }, [
    connections,
    connectionSearch,
    defaultConnectionIds,
    isDefaultConnections,
  ]);

  const hasRoles = React.useMemo(
    () =>
      connections.some(
        (connection) => connection.isStardogCloud || connection.isStardogFree
      ),
    [connections]
  );

  const handleToggleConnection = (endpointId: string) => {
    if (selectedConnectionIds.includes(endpointId)) {
      onSetSelectedConnectionIds(
        selectedConnectionIds.filter((id) => id !== endpointId)
      );
    } else {
      onSetSelectedConnectionIds([...selectedConnectionIds, endpointId]);
    }
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onSetConnectionSearch(e.target.value);
  };

  const handleSearchClear = () => {
    onSetConnectionSearch('');
  };

  return (
    <>
      <div className="invite-org-members-select-connections-controls">
        <InputWrapper
          classNamePrefix="vb-input"
          label={
            copy.components.organization.inviteDialog.selectEndpoints.endpoints
              .label
          }
          labelSize="md"
        >
          <div className="invite-org-members-select-connections-radios">
            <Radio
              checked={!isDefaultConnections}
              onChange={() => onSetIsDefaultConnections(false)}
              size="sm"
              testId={INVITE_ORGANIZATION_DIALOG_CONNECTIONS_SELECT}
            >
              {
                copy.components.organization.inviteDialog.selectEndpoints
                  .endpoints.select
              }
            </Radio>
            <Radio
              checked={isDefaultConnections}
              onChange={() => onSetIsDefaultConnections(true)}
              size="sm"
              testId={INVITE_ORGANIZATION_DIALOG_CONNECTIONS_DEFAULT}
            >
              {copy.components.organization.inviteDialog.selectEndpoints.endpoints.default(
                defaultConnectionIds.size
              )}
            </Radio>
          </div>
        </InputWrapper>

        <Input
          clearable
          clearTestId={INVITE_ORGANIZATION_DIALOG_CONNECTIONS_SEARCH_CLEAR}
          icon={Search}
          label=""
          onChange={handleSearchChange}
          onClear={handleSearchClear}
          placeholder={
            copy.components.organization.inviteDialog.selectEndpoints.table
              .search
          }
          size="md"
          testId={INVITE_ORGANIZATION_DIALOG_CONNECTIONS_SEARCH}
          value={connectionSearch}
        />
      </div>

      <div className="vb-data-table">
        <TableBody
          body={filteredItems.map((connection, idx) => (
            <React.Fragment key={connection.id}>
              <td>
                <div className="invite-org-members-select-connections-cell">
                  <Checkbox
                    checked={
                      defaultConnectionIds.has(connection.id) ||
                      selectedConnectionIds.includes(connection.id)
                    }
                    disabled={defaultConnectionIds.has(connection.id)}
                    onChange={() => handleToggleConnection(connection.id)}
                    size="md"
                    testId={INVITE_ORGANIZATION_DIALOG_CONNECTION_CHECKBOX(
                      connection.id,
                      idx
                    )}
                  />
                  {connection.name}
                </div>
              </td>
              <td>{getEndpointUrl(connection)}</td>
              {hasRoles ? (
                <td>
                  {(connection.isStardogCloud || connection.isStardogFree) &&
                  isDefaultConnections ? (
                    <ChipInput
                      className="invite-org-members-select-connections-roles"
                      clearable={false}
                      disabled
                      label=""
                      size="md"
                      values={defaultRolesByConnectionId[connection.id] || []}
                    />
                  ) : null}
                  {(connection.isStardogCloud || connection.isStardogFree) &&
                  !isDefaultConnections ? (
                    <Selector.Multi<null, SelectItem, SelectItem[]>
                      chipInputProps={{
                        className:
                          'invite-org-members-select-connections-roles',
                        clearable: false,
                        label: '',
                        size: 'md',
                        testId: INVITE_ORGANIZATION_DIALOG_CONNECTION_ROLES(
                          connection.id,
                          idx
                        ),
                        values:
                          selectedConnectionRolesById[connection.id] || [],
                      }}
                      creatable
                      createText={
                        copy.components.organization.inviteDialog
                          .selectEndpoints.table.role.create
                      }
                      disabled={
                        !(
                          defaultConnectionIds.has(connection.id) ||
                          selectedConnectionIds.includes(connection.id)
                        )
                      }
                      filterable
                      items={Array.from(
                        rolesByConnectionId[connection.id]?.roles || []
                      ).map((role) => ({ value: role, text: role }))}
                      itemTestId={
                        INVITE_ORGANIZATION_DIALOG_CONNECTION_ROLE_ITEM
                      }
                      loading={rolesByConnectionId[connection.id]?.isLoading}
                      onSelect={(selected) =>
                        onSetSelectedConnectionRolesByIds((prev) => ({
                          ...prev,
                          [connection.id]: selected.map((item) => item.value),
                        }))
                      }
                      popoverProps={{
                        portalClassName:
                          'invite-organization-members-dialog-menu-portal',
                      }}
                      selected={(
                        selectedConnectionRolesById[connection.id] || []
                      ).map((role) => ({ value: role, text: role }))}
                    />
                  ) : null}
                </td>
              ) : null}
            </React.Fragment>
          ))}
          header={
            <>
              <td>
                {
                  copy.components.organization.inviteDialog.selectEndpoints
                    .table.columns.name
                }
              </td>
              <td>
                {
                  copy.components.organization.inviteDialog.selectEndpoints
                    .table.columns.endpoint
                }
              </td>
              {hasRoles ? (
                <td>
                  {
                    copy.components.organization.inviteDialog.selectEndpoints
                      .table.columns.role
                  }
                </td>
              ) : null}
            </>
          }
          size="lg"
        />
      </div>
    </>
  );
};
